// Smart Sash Admin Panel - Permission Store
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { PermissionDTO } from '@/models';
import { userPermissionService } from '@/services/api/userPermissionService';
import { getErrorMessage } from '@/utils/getErrorMessage';

interface PermissionState {
  userPermissions: PermissionDTO[] | null;
  isLoading: boolean;
  error: string | null;
  fetchUserPermissions: (institutionId: string, userId: string) => Promise<void>;
  replaceUserPermissions: (
    institutionId: string,
    userId: string,
    permissionIds: string[]
  ) => Promise<void>;
  clearPermissions: () => void;
  clearError: () => void;
}

export const usePermissionStore = create<PermissionState>()(
  devtools(
    (set) => ({
      userPermissions: null,
      isLoading: false,
      error: null,
      fetchUserPermissions: async (institutionId: string, userId: string) => {
        set({ isLoading: true, error: null });
        try {
          const permissions = await userPermissionService.getUserPermissions(institutionId, userId);
          set({ userPermissions: permissions, isLoading: false });
        } catch (error: unknown) {
          set({
            error: getErrorMessage(error, 'Failed to fetch user permissions'),
            isLoading: false,
          });
          throw error;
        }
      },
      replaceUserPermissions: async (
        institutionId: string,
        userId: string,
        permissionIds: string[]
      ) => {
        set({ isLoading: true, error: null });
        try {
          const updatedPermissions = await userPermissionService.replaceUserPermissions(
            institutionId,
            userId,
            permissionIds
          );
          set({ userPermissions: updatedPermissions, isLoading: false });
        } catch (error: unknown) {
          set({
            error: getErrorMessage(error, 'Failed to update user permissions'),
            isLoading: false,
          });
          throw error;
        }
      },
      clearPermissions: () => set({ userPermissions: null }),
      clearError: () => set({ error: null }),
    }),
    { name: 'PermissionStore' }
  )
);
